import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { LayoutDashboard, Users, Truck, ShieldAlert, Map, Mail, Settings, BarChart, Receipt, CreditCard, RefreshCcw, Coins, Clock, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Divider from '@/components/ui/divider';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';

const mockTransactions = [
  { id: 'T123', type: 'Commission', amount: '150K GNF', status: 'completed', date: '2025-10-09' },
  { id: 'P456', type: 'Paiement Transporteur', amount: '1.2M GNF', status: 'pending', date: '2025-10-09' },
  { id: 'T789', type: 'Commission', amount: '80K GNF', status: 'completed', date: '2025-10-08' },
  { id: 'R012', type: 'Remboursement', amount: '200K GNF', status: 'processing', date: '2025-10-07' },
  { id: 'P345', type: 'Paiement Client', amount: '950K GNF', status: 'failed', date: '2025-10-06' },
];

const AdminFinancials = () => {
  const handleNotImplemented = () => toast({ title: '🚧 Bientôt disponible!' });
  
  const getStatusBadge = (status) => {
    const statusMap = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    const textMap = { completed: 'Complété', pending: 'En attente', failed: 'Échoué', processing: 'En cours' };
    return <Badge className={`capitalize ${statusMap[status]}`}>{textMap[status]}</Badge>;
  };

  return (
    <>
      <Helmet><title>Gestion Financière - LogiConnect</title></Helmet>
            <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-8">Gestion Financière</h1>
        
        <div className="lg:grid lg:grid-cols-4 lg:gap-6 mb-8">
            <div className="flex space-x-6 overflow-x-auto pb-4 -mx-4 px-4 lg:contents" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                <div className="bg-card border border-border rounded-xl p-6 w-64 flex-shrink-0 lg:w-auto hover:border-secondary transition-colors">
                    <div className="flex justify-between items-start mb-2 min-h-[20px]">
                        <p className="text-sm text-muted-foreground flex-1">Commissions (Mois)</p>
                        <div className="flex-shrink-0">
                            <Coins className="h-4 w-4 text-green-500" />
                        </div>
                    </div>
                    <p className="text-3xl lg:text-4xl font-bold text-foreground mb-1">4.2M GNF</p>
                    <div className={cn("flex items-center text-sm text-green-500")}>
                        <ArrowUp className="w-4 h-4 mr-1" />
                        <span>+15% vs mois</span>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 w-64 flex-shrink-0 lg:w-auto hover:border-secondary transition-colors">
                    <div className="flex justify-between items-start mb-2 min-h-[20px]">
                        <p className="text-sm text-muted-foreground flex-1">Paiements en attente</p>
                        <div className="flex-shrink-0">
                            <Clock className="h-4 w-4 text-yellow-500" />
                        </div>
                    </div>
                    <p className="text-3xl lg:text-4xl font-bold text-foreground mb-1">1.2M GNF</p>
                    <div className={cn("flex items-center text-sm text-red-500")}>
                        <ArrowDown className="w-4 h-4 mr-1" />
                        <span>-5% vs hier</span>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 w-64 flex-shrink-0 lg:w-auto hover:border-secondary transition-colors">
                    <div className="flex justify-between items-start mb-2 min-h-[20px]">
                        <p className="text-sm text-muted-foreground flex-1">Remboursements à traiter</p>
                        <div className="flex-shrink-0">
                            <RefreshCcw className="h-4 w-4 text-blue-500" />
                        </div>
                    </div>
                    <p className="text-3xl lg:text-4xl font-bold text-foreground mb-1">200K GNF</p>
                    <div className={cn("flex items-center text-sm text-green-500")}>
                        <ArrowUp className="w-4 h-4 mr-1" />
                        <span>+2% vs mois</span>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-xl p-6 w-64 flex-shrink-0 lg:w-auto hover:border-secondary transition-colors">
                    <div className="flex justify-between items-start mb-2 min-h-[20px]">
                        <p className="text-sm text-muted-foreground flex-1">Transactions échouées</p>
                        <div className="flex-shrink-0">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                        </div>
                    </div>
                    <p className="text-3xl lg:text-4xl font-bold text-red-400 mb-1">1</p>
                    <div className={cn("flex items-center text-sm text-green-500")}>
                        <ArrowDown className="w-4 h-4 mr-1" />
                        <span>-50% vs hier</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Divider: Stats -> Transactions */}
        <Divider variant="gradient" icon={Receipt} label="Transactions Récentes" />

        <div className="bg-card border border-border rounded-xl">
          <div className="p-6 border-b border-border"><h2 className="text-xl font-semibold text-foreground">Transactions Récentes</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-muted-foreground">
              <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                <tr>
                  <th scope="col" className="px-6 py-3">ID Trans.</th>
                  <th scope="col" className="px-6 py-3">Type</th>
                  <th scope="col" className="px-6 py-3">Montant</th>
                  <th scope="col" className="px-6 py-3">Date</th>
                  <th scope="col" className="px-6 py-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {mockTransactions.map(trans => (
                  <tr key={trans.id} className="border-b border-border hover:bg-muted/50">
                    <td className="px-6 py-4 font-medium text-foreground">{trans.id}</td>
                    <td className="px-6 py-4">{trans.type}</td>
                    <td className="px-6 py-4">{trans.amount}</td>
                    <td className="px-6 py-4">{trans.date}</td>
                    <td className="px-6 py-4">{getStatusBadge(trans.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default AdminFinancials;
